import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  bootstrapSupabaseConfig,
  getSupabase,
  isSupabaseConfigured,
  supabase,
} from '../lib/supabase/client';
import { getAppOrigin } from '../utils/appOrigin';
import {
  clearStudentProfileSession,
  hydrateStudentProfileForUser,
} from '../utils/studentProfileStorage';
import { setActiveStudentUserId } from '../lib/supabase/studentProfileSession';

const AuthContext = createContext(null);

/** Where Supabase redirects after OAuth or magic link. */
function authRedirectUrl() {
  return `${getAppOrigin()}/`;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [configured, setConfigured] = useState(isSupabaseConfigured());

  useEffect(() => {
    let mounted = true;

    bootstrapSupabaseConfig().then((ok) => {
      if (!mounted) return;
      setConfigured(Boolean(ok));
      setConfigLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (configLoading) return undefined;

    if (!isSupabaseConfigured()) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    const client = getSupabase();
    if (!client) {
      setLoading(false);
      return undefined;
    }

    client.auth.getSession().then(({ data }) => {
      if (mounted) {
        setSession(data.session);
        setLoading(false);
      }
    });

    const { data: sub } = client.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [configLoading, configured]);

  const userId = session?.user?.id ?? null;

  useEffect(() => {
    if (configLoading || !configured) return undefined;
    if (!userId) {
      clearStudentProfileSession();
      return undefined;
    }
    setActiveStudentUserId(userId);
    let cancelled = false;
    hydrateStudentProfileForUser(userId).catch((err) => {
      if (!cancelled) console.warn('Student profile hydrate failed:', err);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, configLoading, configured]);

  const signInWithMagicLink = useCallback(async (email) => {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase is not configured.' } };
    }
    return supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: authRedirectUrl(),
        shouldCreateUser: true,
      },
    });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase is not configured.' } };
    }
    const redirectTo = authRedirectUrl();
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    // Ensure browser navigates to Google via Supabase (some environments skip auto-redirect).
    if (!error && data?.url && typeof window !== 'undefined') {
      window.location.assign(data.url);
    }
    return { data, error };
  }, []);

  const signInWithPassword = useCallback(async (email, password) => {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase is not configured.' } };
    }
    return supabase.auth.signInWithPassword({ email: email.trim(), password });
  }, []);

  const signOut = useCallback(async () => {
    if (!isSupabaseConfigured()) return { error: null };
    try {
      const result = await Promise.race([
        supabase.auth.signOut({ scope: 'local' }),
        new Promise((resolve) => {
          window.setTimeout(() => resolve({ error: null }), 2500);
        }),
      ]);
      return result ?? { error: null };
    } catch (err) {
      console.warn('Parashari sign out did not complete cleanly:', err);
      return { error: err };
    }
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      userId,
      loading,
      configLoading,
      configured: configured && isSupabaseConfigured(),
      signInWithMagicLink,
      signInWithGoogle,
      signInWithPassword,
      signOut,
    }),
    [
      session,
      userId,
      loading,
      configLoading,
      configured,
      signInWithMagicLink,
      signInWithGoogle,
      signInWithPassword,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
